import { NO, UIColor, UIRoute, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { RHOutgoingOffer } from "../Core/RHOfferStore"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDateTimeWithLineBreak } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHColors, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHProcurementStatusPresentation } from "../Custom components/RH/Utils_slop/RHProcurementStatusPresentation"
import { RHProcurementWinnerStatus } from "../Custom components/RH/Utils_slop/RHProcurementWinnerStatus"
import { labelForRHViewport } from "../Custom components/RH/Utils_slop/RHResponsiveText"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHTimestampSorting } from "../Custom components/RH/Utils_slop/RHTimestampSorting"
import {
    RHOffererSubmissionOutcomeTokens,
    RHOutgoingOffersViewTokens,
    RHResponsiveTextTokens
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import type { RHOffererSubmissionOutcome } from "../SocketClientFunctions"


type OffererSubmissionOutcomeTextPresentation = {
    text: string
    textColor: UIColor
    fontWeight: string
}


export class OutgoingOffersViewController extends UIViewController {
    
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Esitatud pakkumused",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    readonly unseenBadgeLabel = new UITextView(
        this.view.elementID + "UnseenBadge"
    ).configuredWithObject({
        backgroundColor: RHColor.primary,
        textColor: RHColor.textWhite,
        isSingleLine: true,
        fontSize: 12
    }).addedAsSubviewToView(this.view)
    
    readonly offersDataView = new RHDataView<RHOutgoingOffer>().configuredWithObject({
        noDataMessage: "Esitatud pakkumusi pole",
        descriptors: [
            CBDataViewCellDescriptorWithObject({
                title: "Viitenumber",
                keyPath: "procurement",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    // @ts-ignore - procurement is populated
                    cellView.titleLabel!.text = dataItem.procurement?.referenceNumber?.toString() || "-"
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Hanke nimetus",
                keyPath: "procurement",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    // @ts-ignore - procurement is populated
                    cellView.titleLabel!.text = RHTextSanitizer.escapedHTML(
                        dataItem.procurement?.title || "Nimetus puudub"
                    )
                    // Long titles wrap to (and clamp at) two lines; full title in the tooltip.
                    cellView.configureAsClampedMultilineText()
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Osasid",
                keyPath: "partOffers",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const procurementPartCount = dataItem.procurement?.parts?.length
                    if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(procurementPartCount)) {
                        cellView.titleLabel!.text = ""
                        cellView.backgroundColor = this._rowHighlightColor(dataItem)
                        return
                    }
                    
                    const activeParts = dataItem.partOffers ?? []
                    const submittedPartCount: number = new Set(
                        activeParts
                            .map(partOffer => partOffer.partId?.toString())
                            .filter(Boolean)
                    ).size
                    
                    cellView.titleLabel!.text = submittedPartCount > 0 ? `${submittedPartCount}` : "-"
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Olek",
                keyPath: "status",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.style.fontWeight = RHOffererSubmissionOutcomeTokens.defaultFontWeight
                    // Let long states ("Leping hankeosale on sõlmitud",
                    // "Pakkumus on tühistatud") wrap instead of truncating.
                    cellView.titleLabel!.configureWithObject({
                        isSingleLine: NO,
                        style: { whiteSpace: "normal" }
                    })

                    const presentation = this._statusPresentationForOffer(dataItem)
                    cellView.titleLabel!.text = presentation.text
                    cellView.titleLabel!.textColor = presentation.color
                    cellView.titleLabel!.style.fontWeight = presentation.fontWeight
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Esitamise kuupäev",
                keyPath: "submittedAt",
                allowSorting: YES,
                // Task 5 — default to newest offer first. Production has no mock-up
                // "first contact" interaction record, so the offer's own submit time
                // (descending) is the production-native "newest" key; the shown
                // "Esitamise kuupäev" is this same timestamp.
                initialOrderingState: "descending",
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (dataItem.submittedAt) {
                        const date = new Date(dataItem.submittedAt)
                        cellView.titleLabel!.text =
                            formatRHDateTimeWithLineBreak(date)
                        cellView.titleLabel!.isSingleLine = NO
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Tähtaeg",
                keyPath: "procurement",
                // Not sortable: keyPath is the populated procurement OBJECT, so the
                // sorter would order rows by the object's JSON (ObjectId first), not
                // the displayed deadline — the caret would produce a misleading order.
                allowSorting: NO,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    // @ts-ignore - procurement is populated
                    const deadlineDate = dataItem.procurement?.deadlineDate
                    if (deadlineDate) {
                        const date = new Date(deadlineDate)
                        const isExpired = date < new Date()
                        cellView.titleLabel!.text =
                            formatRHDateTimeWithLineBreak(date)
                        cellView.titleLabel!.isSingleLine = NO
                        cellView.titleLabel!.textColor = isExpired ? RHColor.textMuted : RHColor.textBlack
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                        cellView.titleLabel!.textColor = RHColor.textBlack
                    }
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.estimatedCost),
                keyPath: "procurement",
                // Not sortable: keyPath is the populated procurement OBJECT — numeric
                // comparisons on an object are always false, so the caret would be a
                // silent no-op that only resets the submittedAt order.
                allowSorting: NO,
                dataType: "number",
                // Monetary amounts read best right-aligned (consistent across all tables).
                titleLabelAlignment: UITextView.textAlignment.right,
                cellLabelAlignment: UITextView.textAlignment.right,
                updateCell: (cellView, dataItem) => {
                    // @ts-ignore - procurement is populated
                    const estimatedCost = dataItem.procurement?.estimatedCost
                    cellView.titleLabel!.text = RHEuroFormatter.formattedAmountOrDash(estimatedCost)
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Toimingud",
                keyPath: "_id",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView(),
                updateCell: (cellView, dataItem) => {
                    // Only unread messages bold the action. Outcome remains a row highlight,
                    // mitte badge'iks. Pakkumuste-tulemuse loendur eemaldatud.
                    // @ts-ignore - procurement is populated
                    const procurementId = dataItem.procurement?._id?.toString() ?? ""
                    const hasUnreadMessage =
                        RHCore.sharedInstance.notificationStore.offerMessageCount(procurementId) > 0
                    cellView.customView.text = "Vaata"
                    RHNotificationBadge.applyUnreadEmphasis(
                        cellView.customView.button.titleLabel,
                        hasUnreadMessage,
                        "Lugemata sõnum."
                    )
                    cellView.backgroundColor = this._rowHighlightColor(dataItem)
                    cellView.customView.buttonWasPressed = () => {
                        // @ts-ignore - procurement is populated
                        const procurementId = dataItem.procurement?._id?.toString()
                        if (procurementId) {
                            UIRoute.currentRoute
                                .routeWithViewControllerComponent(
                                    ProcurementDetailsViewController,
                                    {
                                        id: procurementId,
                                        tab: "makeOffer",
                                        backTo: OutgoingOffersViewController.routeComponentName
                                    }
                                )
                                .routeByRemovingComponentNamed(
                                    OutgoingOffersViewController.routeComponentName
                                )
                                .apply()
                        }
                    }
                }
            })
        ]
    }).addedAsSubviewToView(this.view)
    
    readonly bottomSpacerView = new UIView().addedAsSubviewToView(this.view)
    
    _rowHighlightColor(_dataItem: RHOutgoingOffer): UIColor {
        // No per-cell row tint: semi-transparent cell backgrounds left faint vertical
        // seams at the weight-based column edges. The offer outcome is shown in the
        // The "Olek" column keeps outcome presentation separate from unread emphasis.
        return UIColor.transparentColor
    }

    /**
     * Produces the "Olek" cell content following the event table in
     * docs/statuses_and_order_human_table.md. First match wins:
     *   1. server-resolved submission outcome      -> its shared outcome text
     *   2. procurement closed without a contract   -> "Lõpetatud ilma lepinguta" (danger)
     *   3. this offer withdrawn                    -> "Pakkumus on tühistatud"  (danger)
     *   4. this offer is the FULL winner           -> "Märgitud edukaks"       (success)
     *   5. otherwise                               -> "Esitatud"               (primary)
     *
     * Mock-up -> production mapping:
     *   procurement.hasBeenCancelled          -> procurement.status === "closed-without-contract"
     *   this offer's hasTheOfferBeenCancelled -> offer.status === "rescinded"
     *   this tender's isTheWinner (full only) -> RHProcurementWinnerStatus.offerIsFullWinner
     *
     * Server outcomes are authoritative; later branches handle an absent outcome.
     */
    _statusPresentationForOffer(offer: RHOutgoingOffer): {
        text: string
        color: UIColor
        fontWeight: string
    } {
        const outcomePresentation = OutgoingOffersViewController._presentationForOutcome(
            offer.offererSubmissionOutcome
        )
        if (outcomePresentation) {
            return {
                text: outcomePresentation.text,
                color: outcomePresentation.textColor,
                fontWeight: outcomePresentation.fontWeight
            }
        }

        const procurement = offer.procurement
        const isProcurementClosedWithoutContract = procurement?.status === "closed-without-contract"
        // `status` is typed on RHProcurementOffer ("draft" | "submitted" |
        // "rescinded"), so no cast is needed here.
        const isOfferWithdrawn = offer.status === "rescinded"

        if (isProcurementClosedWithoutContract) {
            return {
                text: "Lõpetatud ilma lepinguta",
                color: RHColor.danger,
                fontWeight: RHOffererSubmissionOutcomeTokens.defaultFontWeight
            }
        }

        if (isOfferWithdrawn) {
            return {
                text: "Pakkumus on tühistatud",
                color: RHColor.danger,
                fontWeight: RHOffererSubmissionOutcomeTokens.defaultFontWeight
            }
        }

        if (this._offerIsFullWinner(offer)) {
            return {
                text: "Märgitud edukaks",
                color: RHColor.success,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        return {
            text: "Esitatud",
            color: RHColor.primary,
            fontWeight: RHOffererSubmissionOutcomeTokens.defaultFontWeight
        }
    }

    /**
     * YES only when this offer holds the winning part offer for EVERY part of the
     * procurement (won the whole procurement). Part-only winners return NO so they
     * fall through to "Esitatud".
     */
    private _offerIsFullWinner(dataItem: RHOutgoingOffer): boolean {
        const procurement = dataItem.procurement
        const partOffers = (dataItem.partOffers ?? []) as { _id?: unknown }[]
        const offerPartOfferIds = new Set(
            partOffers
                .map(partOffer => partOffer?._id?.toString())
                .filter((id): id is string => !!id)
        )
        return RHProcurementWinnerStatus.offerIsFullWinner(procurement, offerPartOfferIds)
    }

    /**
     * Keeps only the latest state per procurement in the offerer's submitted-offers
     * list (statuses_and_order_human_table.md, events 6 and 9). A withdrawn
     * (rescinded) record is dropped whenever ANY record for the same procurement —
     * submitted or rescinded — has a strictly newer submit time:
     *   - withdraw -> re-submit         => only the newer submitted row stays;
     *   - withdraw -> re-submit -> withdraw (repeatedly)
     *                                   => only the single newest withdrawn row
     *                                      stays, never several "Pakkumus on
     *                                      tühistatud" rows for one procurement.
     * Active (submitted) records are never dropped — at most one can exist per
     * procurement at a time, and an active offer must never be hidden by stale
     * withdrawal records.
     *
     * Every offer here already belongs to the current user's company (the server
     * query filters by `offererCompany`), so "same company" is implicit. Derived
     * purely from the existing offer list — no schema, migration, or new server
     * field.
     */
    private _offersWithSupersededWithdrawalsRemoved(offers: RHOutgoingOffer[]): RHOutgoingOffer[] {
        const newestSubmitTimeByProcurement = new Map<string, number>()
        for (const offer of offers) {
            const procurementId = this._procurementIdForOffer(offer)
            const submitTime = this._offerSubmitTimeMilliseconds(offer)
            if (!procurementId || submitTime === undefined) {
                continue
            }
            const newestSoFar = newestSubmitTimeByProcurement.get(procurementId)
            if (newestSoFar === undefined || submitTime > newestSoFar) {
                newestSubmitTimeByProcurement.set(procurementId, submitTime)
            }
        }
        return offers.filter(offer => {
            if (offer.status !== "rescinded") {
                return YES
            }
            const procurementId = this._procurementIdForOffer(offer)
            if (!procurementId) {
                return YES
            }
            const newestSubmitTime = newestSubmitTimeByProcurement.get(procurementId)
            const withdrawnSubmitTime = this._offerSubmitTimeMilliseconds(offer)
            if (newestSubmitTime === undefined || withdrawnSubmitTime === undefined) {
                return YES
            }
            // Keep the withdrawal only while nothing strictly newer exists for the
            // same procurement — i.e. this withdrawal IS the latest state.
            return withdrawnSubmitTime >= newestSubmitTime
        })
    }

    private _procurementIdForOffer(offer: RHOutgoingOffer): string | undefined {
        // procurement is populated on outgoing offers (typed as a reference).
        const procurement = offer.procurement
        return procurement?._id?.toString()
    }

    private _offerSubmitTimeMilliseconds(offer: RHOutgoingOffer): number | undefined {
        if (!offer.submittedAt) {
            return undefined
        }
        const time = new Date(offer.submittedAt).getTime()
        if (isNaN(time)) {
            return undefined
        }
        return time
    }

    
    private _unseenCountDidChangeHandler = (_event: UIViewBroadcastEvent) => {
        this._updateUnseenBadge()
    }
    
    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        RHNotificationBadge.configurePageLabel(this.unseenBadgeLabel)
        this.unseenBadgeLabel.hidden = true
        this._applyTableColumnLayout()
    }
    
    
    private _applyTableColumnLayout() {
        this.offersDataView.tableHeaderView.configureWithObject({
            cellWeights: RHOutgoingOffersViewTokens.columnWeights,
            intrinsicContentHeight: () => 64
        })
        this.offersDataView.tableView.configureWithObject({
            cellWeights: RHOutgoingOffersViewTokens.columnWeights
        })
        this._configureTableHeaderCells()
        this.offersDataView.tableHeaderView.setNeedsLayout()
        this.offersDataView.tableView.setNeedsLayout()
    }
    
    private _configureTableHeaderCells() {
        for (const headerCell of this.offersDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHOutgoingOffersViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHOutgoingOffersViewTokens.multilineHeaderLineHeight
        }
    }
    
    
    static override readonly routeComponentName = "outgoing_offers"
    static override readonly ParameterIdentifierName = {}
    static readonly requiredApplicationModes: ("procurer" | "offerer")[] = ["offerer"]
    
    override async viewWillAppear() {
        await super.viewWillAppear()
        RHCore.sharedInstance.offerStore.subscribe(this, () => {
            this._reloadData()
        })
        RHCore.sharedInstance.notificationStore.subscribe(this, (affectedIds) => {
            if (affectedIds.length > 0) {
                // Acknowledgement state patches RHOfferStore's cached row
                // metadata before this callback. Rebuild the data-view copies
                // so requiresOffererAttention cannot remain stale.
                void this._reloadData()
                return
            }
            this.offersDataView.tableView.reloadData()
        })
        RHCore.sharedInstance.offerStore.invalidateOutgoingOffers()
        this.view.performFunctionAfterBroadcastEvent(
            RHCore.broadcastEventName.unseenEventCountDidChange,
            this._unseenCountDidChangeHandler
        )
    }
    
    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHCore.sharedInstance.offerStore.unsubscribe(this)
        RHCore.sharedInstance.notificationStore.unsubscribe(this)
        this.view.removePerformFunctionAfterBroadcastEvent(
            RHCore.broadcastEventName.unseenEventCountDidChange,
            this._unseenCountDidChangeHandler
        )
    }
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        // Read-only nav badge refresh — opening this list marks nothing as seen.
        RHCore.sharedInstance.notificationStore.refreshGlobalCounts()
        RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
        this.view.setNeedsLayout()
    }

    _updateUnseenBadge() {
        // Page-pill "N uut" eemaldatud lihtsustamise käigus — hoia peidetud.
        this.unseenBadgeLabel.hidden = true
        this.view.setNeedsLayout()
    }
    
    async _reloadData() {
        try {
            const offers = await RHCore.sharedInstance.offerStore.getOutgoingOffers()
            // Only the latest state per procurement matters: drop withdrawn
            // (rescinded) offers superseded by anything newer — a re-submission or
            // a newer withdrawal — so one procurement never shows several
            // "tühistatud" rows or both a "tühistatud" and an "esitatud" row.
            const latestStateOffers = this._offersWithSupersededWithdrawalsRemoved(offers ?? [])
            // Normalise submittedAt to a canonical ISO string so the data view's
            // string-based descending sort reliably means "newest submitted first".
            this.offersDataView.data = latestStateOffers.map(offer => ({
                ...offer,
                submittedAt: RHTimestampSorting.sortableISOString(offer.submittedAt)
            }))
        }
        catch (error) {
            console.error("OutgoingOffersViewController: error loading offers:", error)
            this.offersDataView.data = []
        }
        this._updateUnseenBadge()
    }
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
    }
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const badgeHeight = RHBadges.notificationPageHeight
        const badgePaddingH = RHBadges.notificationPagePaddingX
        const bounds = this.view.contentBoundsWithInset(padding)
        
        const titleFrame = bounds.rectangleWithHeight(labelHeight)
        this.titleLabel.frame = titleFrame
        
        if (!this.unseenBadgeLabel.hidden) {
            const badgeWidth = this.unseenBadgeLabel.intrinsicContentWidth(badgeHeight) + badgePaddingH * 2
            this.unseenBadgeLabel.frame = titleFrame
                .rectangleWithWidth(badgeWidth, 1)
                .rectangleWithHeight(badgeHeight, 0.5)
            this.unseenBadgeLabel.setBorder(badgeHeight / 2, 0)
        }
        
        titleFrame
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.offersDataView)
            .rectangleForNextRow(padding, this.bottomSpacerView)
            .assignedAsFrameOfView(this.bottomSpacerView)
    }


    static _presentationForOutcome(
        outcome?: RHOffererSubmissionOutcome
    ): OffererSubmissionOutcomeTextPresentation | undefined {
        if (!outcome) {
            return undefined
        }
        const text = RHProcurementStatusPresentation.textForOffererSubmissionOutcome(outcome)

        if (outcome === "evaluating") {
            return {
                text,
                textColor: RHTables.rowTextColor,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "winner") {
            return {
                text,
                textColor: RHColors.winnerGold,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "partialWinner") {
            return {
                text,
                textColor: RHColors.winnerGold,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "multiplePartsWinner") {
            return {
                text,
                textColor: RHColors.winnerGold,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "singlePartEvaluationFinalized") {
            return {
                text,
                textColor: RHTables.rowTextColor,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "multiplePartsEvaluationFinalized") {
            return {
                text,
                textColor: RHTables.rowTextColor,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "partContractSigned") {
            return {
                text,
                textColor: RHColors.successGreen,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "partContractsSigned") {
            return {
                text,
                textColor: RHColors.successGreen,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "contractSigned") {
            return {
                text,
                textColor: RHColors.successGreen,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (
            outcome === "partContractCompleted" ||
            outcome === "partContractsCompleted"
        ) {
            return {
                text,
                textColor: RHColor.textBlack,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "contractNotConcluded") {
            return {
                text,
                textColor: RHTables.rowTextColor,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "notWinner") {
            return {
                text,
                textColor: RHColors.dangerRed,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        if (outcome === "noWinner") {
            return {
                text,
                textColor: RHTables.rowTextColor,
                fontWeight: RHOffererSubmissionOutcomeTokens.outcomeFontWeight
            }
        }

        const unhandledOutcome: never = outcome
        return unhandledOutcome
    }

}
